import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  /**
   * Safely stores data in localStorage with error handling
   * @param key The storage key
   * @param value The value to store (will be JSON stringified)
   * @returns true if successful, false if failed
   */
  setItem(key: string, value: any): boolean {
    try {
      // Check if localStorage is accessible at all
      if (!this.canAccessLocalStorage()) {
        return false;
      }

      const serializedValue = JSON.stringify(value);
      window.localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      // Try cleanup and retry once on quota exceeded
      if (error.name === 'QuotaExceededError') {
        console.warn(
          `localStorage quota exceeded for key "${key}". Attempting cleanup...`
        );

        if (this.cleanupPlayerStorage()) {
          console.info('Player storage cleanup completed. Retrying save...');

          // Retry once after cleanup
          try {
            const serializedValue = JSON.stringify(value);
            window.localStorage.setItem(key, serializedValue);
            return true;
          } catch (retryError) {
            console.warn(
              `Retry failed after cleanup for key "${key}":`,
              retryError
            );
          }
        }
      }

      this.handleStorageError(error, 'setItem', key);
      return false;
    }
  }

  /**
   * Safely retrieves and parses data from localStorage
   * @param key The storage key
   * @param defaultValue Default value to return if retrieval fails
   * @returns The parsed value or default value
   */
  getItem<T>(key: string, defaultValue: T = null): T {
    try {
      // Check if localStorage is accessible at all
      if (!this.canAccessLocalStorage()) {
        return defaultValue;
      }

      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      this.handleStorageError(error, 'getItem', key);
      return defaultValue;
    }
  }

  /**
   * Safely removes an item from localStorage
   * @param key The storage key
   * @returns true if successful, false if failed
   */
  removeItem(key: string): boolean {
    try {
      // Check if localStorage is accessible at all
      if (!this.canAccessLocalStorage()) {
        return false;
      }

      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      this.handleStorageError(error, 'removeItem', key);
      return false;
    }
  }

  /**
   * Checks if localStorage is available and has space
   * @returns true if localStorage is usable
   */
  private isStorageAvailable(): boolean {
    try {
      // First check if we can access localStorage at all
      if (!this.canAccessLocalStorage()) {
        return false;
      }

      const testKey = '__storage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Gets the approximate size of localStorage usage in bytes
   * @returns Size in bytes or -1 if unavailable
   */
  getStorageSize(): number {
    try {
      let total = 0;
      for (const key in window.localStorage) {
        if (window.localStorage.hasOwnProperty(key)) {
          total += window.localStorage[key].length + key.length;
        }
      }
      return total;
    } catch (error) {
      return -1;
    }
  }

  /**
   * Attempts to free up space by removing old player data
   * @returns true if cleanup was performed
   */
  cleanupPlayerStorage(): boolean {
    try {
      // Remove old player data that might be taking up space
      const keysToCheck = ['player-queue', 'player-current'];
      let cleaned = false;

      for (const key of keysToCheck) {
        if (window.localStorage.getItem(key)) {
          window.localStorage.removeItem(key);
          cleaned = true;
        }
      }

      return cleaned;
    } catch (error) {
      return false;
    }
  }

  /**
   * Safely checks if localStorage is accessible
   * @returns true if localStorage can be accessed
   */
  private canAccessLocalStorage(): boolean {
    try {
      // Check if window exists and localStorage is available and not null
      if (!window?.localStorage) {
        return false;
      }

      // Try to actually access localStorage to catch more edge cases
      window.localStorage.getItem('__test__');
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Handles storage errors with appropriate logging and user feedback
   */
  private handleStorageError(error: any, operation: string, key: string): void {
    if (error.name === 'QuotaExceededError') {
      console.warn(
        `localStorage quota exceeded during ${operation} for key "${key}".`
      );
    } else if (error.name === 'SecurityError') {
      console.warn(
        `localStorage access denied during ${operation} for key "${key}". This may be due to private browsing mode.`
      );
    } else {
      console.warn(
        `localStorage error during ${operation} for key "${key}":`,
        error
      );
    }
  }
}
